import PropTypes from 'prop-types'

import styled from '@emotion/styled'
import {
  fromUTCtoZoneFormatted,
  STORE_KEY_LIST,
  storeGet
} from 'mailup-react-helpers'
import {
  Accordion,
  Collapsible,
  Divider,
  Flex,
  FlexItem,
  Grid,
  GRID_VARIANT_COLLAPSED,
  GridCell,
  Icon,
  Link,
  LINK_VARIANT_DEFAULT,
  Panel,
  SPACING_16,
  SPACING_8,
  SpacingVertical,
  Text,
  TEXT_VARIANT_BOLD_LIGHT,
  TEXT_VARIANT_LIGHT,
  TEXT_VARIANT_SMALL_LIGHT,
  Thumb,
  ThumbContainer,
  ThumbDefaultOverlay,
  Title,
  TITLE_VARIANT_M,
  TITLE_VARIANT_S
} from 'mailup-web-components'
import { useState } from 'react'
import { useIntl } from 'react-intl'

const StyledPanel = styled(Panel)`
  padding: 0 !important;
  border-radius: 0 !important;
`

const StyledGridCellGrey = styled(GridCell)`
  background-color: #f5f5f5 !important;
`

const StyledIcon = styled(Icon)`
  color: #39ba7c;
  font-size: 17px;
  margin: 2px 4px 0 0;
`

const StyledTextNoteItalic = styled(Text)`
  font-style: italic !important;
  word-break: break-all;
`

const StyledLink = styled(Link)`
  word-break: break-all;
`

const StyledTableCustomFields = styled.table`
  width: 100%;
  background-color: white;
  border-color: #d5dadf;
  border-width: 1px;
  border-style: solid;
  border-collapse: collapse;
  box-sizing: border-box;
  td {
    color: #7c7c7c;
    word-wrap: break-word;
    padding: 10px 15px;
    border-color: #d5dadf;
    border-width: 1px;
    border-style: solid;
  }
  td:nth-of-type(1) {
    text-align: center !important;
    width: 8%;
  }
  td:nth-of-type(2) {
    width: 46%;
  }
  td:nth-of-type(3) {
    width: 46%;
    word-break: break-all;
  }
`

const Row = ({ item }) => {
  const intl = useIntl()
  const [showExtra, setSwohExtra] = useState(false)
  const { TimeZoneCode: tzcode } = storeGet(STORE_KEY_LIST)

  const openLinkURL = () => {
    window.open(item.LinkURL, '_blank', 'noopener,noreferrer')
  }

  const onClickPreview = () => {
    window.open(item.Image, '_blank', 'noopener,noreferrer')
  }

  const Overlay = () => {
    return <ThumbDefaultOverlay />
  }

  return (
    <>
      <StyledPanel>
        <Grid cols={12} variant={GRID_VARIANT_COLLAPSED}>
          {item.Image && (
            <GridCell span={2}>
              <Flex padding=' 24px 0px 16px 32px'>
                <div
                  data-tip={intl.formatMessage({
                    id: 'Load.ContentsSourcesViewContents.ImageTooltip'
                  })}
                >
                  <ThumbContainer>
                    <Thumb
                      aspectRatio={0}
                      HoverComponent={Overlay}
                      onClick={onClickPreview}
                    >
                      <img src={item.Image} />
                    </Thumb>
                  </ThumbContainer>
                </div>
              </Flex>
            </GridCell>
          )}

          <GridCell span={item.Image ? 7 : 9}>
            <Flex
              padding='24px 32px 16px 32px'
              flexDirection='column'
              rowGap='16px'
            >
              <Flex alignItems='baseline'>
                <StyledIcon family='sli' icon='key-1' />
                <StyledTextNoteItalic
                  htmlElement='span'
                  variant={TEXT_VARIANT_SMALL_LIGHT}
                  data-tip={intl.formatMessage({
                    id: 'Load.ContentsSourcesViewContents.ContentKeyTooltip'
                  })}
                >
                  {item.ContentKey}
                </StyledTextNoteItalic>
              </Flex>
              {item.Title && (
                <>
                  <Title
                    variant={TITLE_VARIANT_M}
                    data-tip={intl.formatMessage({
                      id: 'Load.ContentsSourcesViewContents.TitleTooltip'
                    })}
                  >
                    {item.Title}
                  </Title>
                </>
              )}
              {item.Summary && (
                <>
                  <Text
                    htmlElement='span'
                    variant={TEXT_VARIANT_LIGHT}
                    data-tip={intl.formatMessage({
                      id: 'Load.ContentsSourcesViewContents.SummaryTooltip'
                    })}
                  >
                    {item.Summary}
                  </Text>
                </>
              )}
              {item.LinkURL && (
                <>
                  <StyledLink
                    variant={LINK_VARIANT_DEFAULT}
                    onClick={openLinkURL}
                    data-tip={intl.formatMessage({
                      id: 'Load.ContentsSourcesViewContents.LinkURLTooltip'
                    })}
                  >
                    {item.LinkURL}
                  </StyledLink>
                </>
              )}
              <Collapsible collapsed={!showExtra}>
                <Flex flexDirection='column' rowGap='16px'>
                  {item.LinkText && (
                    <>
                      <Grid>
                        <GridCell span={3}>
                          <Title variant={TITLE_VARIANT_S}>
                            {intl.formatMessage({
                              id: 'Load.ContentsSourcesViewContents.LinkText'
                            })}
                          </Title>
                        </GridCell>
                        <GridCell span={9}>
                          <Text
                            htmlElement='span'
                            variant={TEXT_VARIANT_LIGHT}
                            data-tip={intl.formatMessage({
                              id: 'Load.ContentsSourcesViewContents.LinkTextTooltip'
                            })}
                          >
                            {item.LinkText}
                          </Text>
                        </GridCell>
                      </Grid>
                    </>
                  )}
                  {item.SubTitle && (
                    <>
                      <Grid>
                        <GridCell span={3}>
                          <Title variant={TITLE_VARIANT_S}>
                            {intl.formatMessage({
                              id: 'Load.ContentsSourcesViewContents.SubTitle'
                            })}
                          </Title>
                        </GridCell>
                        <GridCell span={9}>
                          <Text
                            htmlElement='span'
                            variant={TEXT_VARIANT_LIGHT}
                            data-tip={intl.formatMessage({
                              id: 'Load.ContentsSourcesViewContents.SubTitleTooltip'
                            })}
                          >
                            {item.SubTitle}
                          </Text>
                        </GridCell>
                      </Grid>
                    </>
                  )}
                  {item.Author && (
                    <>
                      <Grid>
                        <GridCell span={3}>
                          <Title variant={TITLE_VARIANT_S}>
                            {intl.formatMessage({
                              id: 'Load.ContentsSourcesViewContents.Author'
                            })}
                          </Title>
                        </GridCell>
                        <GridCell span={9}>
                          <Text
                            htmlElement='span'
                            variant={TEXT_VARIANT_LIGHT}
                            data-tip={intl.formatMessage({
                              id: 'Load.ContentsSourcesViewContents.AuthorTooltip'
                            })}
                          >
                            {item.Author}
                          </Text>
                        </GridCell>
                      </Grid>
                    </>
                  )}
                  {item.Category && (
                    <>
                      <Grid>
                        <GridCell span={3}>
                          <Title variant={TITLE_VARIANT_S}>
                            {intl.formatMessage({
                              id: 'Load.ContentsSourcesViewContents.Category'
                            })}
                          </Title>
                        </GridCell>
                        <GridCell span={9}>
                          <Text
                            htmlElement='span'
                            variant={TEXT_VARIANT_LIGHT}
                            data-tip={intl.formatMessage({
                              id: 'Load.ContentsSourcesViewContents.CategoryTooltip'
                            })}
                          >
                            {item.Category}
                          </Text>
                        </GridCell>
                      </Grid>
                    </>
                  )}
                  {item.CustomFields && item.CustomFields.length > 0 && (
                    <FlexItem>
                      <Title variant={TITLE_VARIANT_S}>
                        {intl.formatMessage({
                          id: 'Load.ContentsSourcesViewContents.CustomFields'
                        })}
                      </Title>
                      <SpacingVertical variant={SPACING_8} />
                      <div>
                        <StyledTableCustomFields
                          data-tip={intl.formatMessage({
                            id: 'Load.ContentsSourcesViewContents.CustomFieldsTooltip'
                          })}
                        >
                          {item.CustomFields.map((customField, key) => (
                            <tr key={key}>
                              <td className='text-center'>
                                <Text
                                  variant={TEXT_VARIANT_BOLD_LIGHT}
                                  htmlElement='span'
                                >
                                  {key + 1}{' '}
                                </Text>
                              </td>
                              <td>
                                <Text
                                  variant={TEXT_VARIANT_BOLD_LIGHT}
                                  htmlElement='span'
                                >
                                  Name:{' '}
                                </Text>
                                {customField.Name}
                              </td>
                              <td className='container_word-to-break'>
                                <Text
                                  variant={TEXT_VARIANT_BOLD_LIGHT}
                                  htmlElement='span'
                                >
                                  Value:{' '}
                                </Text>
                                {customField.Value}
                              </td>
                            </tr>
                          ))}
                        </StyledTableCustomFields>
                      </div>
                    </FlexItem>
                  )}
                </Flex>
              </Collapsible>
            </Flex>
          </GridCell>

          <StyledGridCellGrey span={3}>
            <Flex flexDirection='column' padding='24px 32px 16px 32px'>
              <Flex flexDirection='column' rowGap='4px'>
                <StyledIcon family='sli' icon='calendar' />{' '}
                <Text variant={TEXT_VARIANT_BOLD_LIGHT}>
                  {intl.formatMessage({
                    id: 'Load.ContentsSourcesViewContents.ContentDate'
                  })}
                </Text>
                <Text
                  variant={TEXT_VARIANT_LIGHT}
                  htmlElement='span'
                  data-tip={intl.formatMessage({
                    id: 'Load.ContentsSourcesViewContents.ContentDateTooltip'
                  })}
                >
                  {fromUTCtoZoneFormatted(
                    item.ContentDate,
                    tzcode,
                    'YYYY-MM-DD HH:mm'
                  )}
                </Text>
              </Flex>
              <SpacingVertical variant={SPACING_16} />
              <Flex flexDirection='column' rowGap='4px'>
                <StyledIcon family='sli' icon='upload-2' />
                <Text variant='bold-light'>
                  {intl.formatMessage({
                    id: 'Load.ContentsSourcesViewContents.ContentLoadingDate'
                  })}
                </Text>
                <Text
                  variant={TEXT_VARIANT_LIGHT}
                  htmlElement='span'
                  data-tip={intl.formatMessage({
                    id: 'Load.ContentsSourcesViewContents.ContentLoadingDateTooltip'
                  })}
                >
                  {fromUTCtoZoneFormatted(
                    item.ContentLoadingDate,
                    tzcode,
                    'YYYY-MM-DD HH:mm'
                  )}
                </Text>
              </Flex>
            </Flex>
          </StyledGridCellGrey>
        </Grid>
        <Divider />
        <Flex height='42px' justifyContent='center' alignItems='center'>
          <div onClick={() => setSwohExtra(!showExtra)}>
            <Accordion
              data-mu-test='test-accordion'
              label={
                showExtra
                  ? intl.formatMessage({
                      id: 'Load.ContentsSourcesViewContents.HideFields'
                    })
                  : intl.formatMessage({
                      id: 'Load.ContentsSourcesViewContents.ShowFields'
                    })
              }
            />
          </div>
        </Flex>
      </StyledPanel>
    </>
  )
}

Row.propTypes = {
  item: PropTypes.object
}

export default Row
